import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import MakeGetRequest from "../utils/MakeGetRequest";
import makePostRequest from "../utils/makePostRequest";
import makeDeleteRequest from "../utils/makeDeleteRequest";
import SmallTitleText from "../SmallTitleText";
import FormInputField from "../FormInputField";
import PopUpModulBtn from "../PopUpModulBtn";
import RichTextEditorField from "../RichTextEditorField";

const getPlainTextFromHtml = (value = "") =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CreateEventForm = () => {
  const [postLoading, setPostLoading] = useState(false);
  const [postSuccessMsg, setPostSuccessMsg] = useState("");
  const [postError, setPostError] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [performerInput, setPerformerInput] = useState("");
  const [performerSuggestions, setPerformerSuggestions] = useState([]);
  const [performerTags, setPerformerTags] = useState([]);

  const navigate = useNavigate();
  const formRef = useRef(null);
  const nameRef = useRef(null);
  const dateRef = useRef(null);
  const authorRef = useRef(null);

  // Get the location to see if its a new event or event edit.
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const slug = searchParams.get("slug");
  const apiRoute = slug ? `get_event_form?slug=${slug}` : "get_event_form";

  const { data: formData, loading: formLoading } = MakeGetRequest({
    route: apiRoute,
    forceToken: true,
    errorCodeRedirect: 404,
  });

  const apiUrl = import.meta.env.VITE_API_URL;
  const minEventDateTime = (() => {
    const oneDayFromNow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    oneDayFromNow.setSeconds(0, 0);
    const timezoneOffset = oneDayFromNow.getTimezoneOffset() * 60 * 1000;
    return new Date(oneDayFromNow.getTime() - timezoneOffset)
      .toISOString()
      .slice(0, 16);
  })();

  useEffect(() => {
    if (formData?.current_event) {
      const existingPerformers = formData.current_event.performers;
      const existingPerformerIds = formData.current_event.performer_ids;

      const getPerformerIdByName = (performerName) => {
        if (!performerName) {
          return null;
        }

        const matchedPerformer = formData?.performers?.find(
          (performer) =>
            performer?.name?.toLowerCase() === performerName.toLowerCase(),
        );

        return matchedPerformer?.id ?? null;
      };

      const getPerformerNameById = (performerId) => {
        const matchedPerformer = formData?.performers?.find(
          (performer) => String(performer?.id) === String(performerId),
        );

        return matchedPerformer?.name ?? null;
      };

      const normalizePerformerTags = () => {
        if (Array.isArray(existingPerformers)) {
          return existingPerformers
            .map((performer) => {
              if (typeof performer === "string") {
                const name = performer.trim();
                const id = getPerformerIdByName(name);
                return { name, id, isDb: Boolean(id) };
              }

              if (performer && typeof performer === "object") {
                const name = (
                  performer.name ||
                  performer.band_name ||
                  ""
                ).trim();
                const id = performer.id ?? getPerformerIdByName(name);
                return { name, id, isDb: Boolean(id) };
              }

              return { name: "", id: null, isDb: false };
            })
            .filter((tag) => tag.name.length > 0);
        }

        if (typeof existingPerformers === "string") {
          return existingPerformers
            .split(",")
            .map((name) => name.trim())
            .filter((name) => name.length > 0)
            .map((name) => {
              const id = getPerformerIdByName(name);
              return {
                name,
                id,
                isDb: Boolean(id),
              };
            });
        }

        if (Array.isArray(existingPerformerIds)) {
          return existingPerformerIds
            .map((id) => {
              const name = getPerformerNameById(id);
              return {
                name: name || String(id),
                id,
                isDb: Boolean(name),
              };
            })
            .filter((tag) => tag.name.length > 0);
        }

        return [];
      };

      const initialTags = normalizePerformerTags();

      setPerformerTags(initialTags);
      setPerformerInput("");
      setDescriptionValue(formData.current_event.description || "");
    }
  }, [formData]);

  const addPerformerTags = (value) => {
    const names = value
      .split(",")
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    if (!names.length) {
      return;
    }

    setPerformerTags((prevTags) => {
      const updatedTags = [...prevTags];

      names.forEach((name) => {
        const matchedPerformer = formData?.performers?.find(
          (performer) => performer?.name?.toLowerCase() === name.toLowerCase(),
        );

        const matchedId = matchedPerformer?.id ?? null;
        const alreadyExists = updatedTags.some(
          (tag) => tag.name.toLowerCase() === name.toLowerCase(),
        );

        if (!alreadyExists) {
          const isDb = performerSuggestions.some(
            (suggestion) => suggestion.toLowerCase() === name.toLowerCase(),
          );

          updatedTags.push({
            name,
            id: matchedId,
            isDb: isDb || Boolean(matchedId),
          });
        }
      });

      return updatedTags;
    });

    setPerformerInput("");
  };

  const removePerformerTag = (nameToRemove) => {
    setPerformerTags((prevTags) =>
      prevTags.filter((tag) => tag.name !== nameToRemove),
    );
  };

  useEffect(() => {
    const searchTerm = performerInput.trim();

    if (searchTerm.length < 2) {
      setPerformerSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${apiUrl}search?page=1&q=${encodeURIComponent(searchTerm)}&include=bands`,
        );

        const bandNames =
          response?.data?.results
            ?.filter((item) => item?.type === "Profil" && item?.title)
            .map((item) => item.title)
            .filter((name, index, allNames) => allNames.indexOf(name) === index)
            .slice(0, 8) || [];

        setPerformerSuggestions(bandNames);
      } catch (error) {
        setPerformerSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [performerInput, apiUrl]);

  // Can only view event if it has name
  const eventUrl =
    formData?.current_event.name && slug ? `/events/${slug}` : "";

  const handleSubmit = (e) => {
    e.preventDefault();
    setPostError("");
    setPostSuccessMsg("");

    const descriptionPlainText = getPlainTextFromHtml(descriptionValue);
    if (!descriptionPlainText) {
      setPostError("Beskrivningen kan inte vara tom");
      return;
    }

    if (formRef.current.checkValidity()) {
      // Create a FormData object
      const newData = new FormData();
      // Get all form elements
      const formElements = formRef.current.elements;

      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i];
        if (element.name) {
          switch (element.type) {
            case "file":
              if (element.files.length > 0) {
                // Handle file input
                const fileSizeInMB = element.files[0].size / (1024 * 1024);
                if (fileSizeInMB > 4) {
                  setPostError("Filstorleken är för stor");
                  return;
                }
                newData.append(element.name, element.files[0]);
              }
              break;

            default:
              switch (element.name) {
                case "posted_by_id":
                case "price":
                case "age_limit":
                case "region":
                case "city":
                  // Parse integers
                  if (element.value) {
                    newData.append(element.name, parseInt(element.value, 10));
                  }
                  break;

                case "performer_ids":
                  // Handle performer IDs if needed
                  if (element.value) {
                    newData.append(element.name, element.value);
                  }
                  break;

                default:
                  if (element.value) {
                    newData.append(element.name, element.value);
                  }
                  break;
              }
              break;
          }
        }
      }

      newData.append(
        "performers_data",
        JSON.stringify(
          performerTags.map((tag) => ({
            name: tag.name,
            id: tag.id,
          })),
        ),
      );

      const performerIds = performerTags
        .map((tag) => tag.id)
        .filter((id) => id !== null && id !== undefined);

      if (performerIds.length > 0) {
        newData.append("performer_ids", performerIds.join(","));
      }

      newData.append("description", descriptionValue);

      const currentEventSlug = formData?.current_event?.slug || slug;
      if (currentEventSlug) {
        // Send both keys for backward compatibility with backend fallback logic.
        newData.append("current_event_slug", currentEventSlug);
        newData.append("slug", currentEventSlug);
      }

      setPostLoading(true);
      makePostRequest("create_event", newData)
        .then((response) => {
          setPostLoading(false);
          if (response.error) {
            setPostError(response.error);
          } else {
            const backendMessage = response?.data?.message;

            if (backendMessage) {
              setPostSuccessMsg(backendMessage);
            } else if (currentEventSlug) {
              setPostSuccessMsg("Event uppdaterat");
            } else {
              setPostSuccessMsg("Event skapat");
            }

            // Redirect only after creating a new event.
            if (!currentEventSlug) {
              setTimeout(() => {
                navigate("/userprofile");
              }, 2500);
            }
          }
        })
        .catch(() => {
          // handle errors
          setPostError("Ett fel uppstod");
          setPostLoading(false);
        });
    } else {
      // form error
      formRef.current.reportValidity();
    }
  };

  const handleDeleteEvent = (e) => {
    e.preventDefault();
    makeDeleteRequest({
      route: `delete_event?slug=${slug}`,
      forceToken: true,
      redirectSuccessPath: "/userprofile",
    });
  };

  if (formLoading) {
    return <div>Loading form data...</div>;
  }

  if (!formLoading) {
    return (
      <>
        <div className="p-4">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target?.name === "date") {
                e.preventDefault();
              }
            }}
            noValidate
          >
            {formData && (
              <>
                {formData.current_event.id ? (
                  <>
                    <SmallTitleText text="Uppdatera evenemang" />
                    <p className="mt-4">
                      Skapat: {formData.current_event.created_at}
                    </p>
                    {formData.current_event.created_at !=
                      formData.current_event.updated_at && (
                      <p>Uppdaterat: {formData.current_event.updated_at}</p>
                    )}
                  </>
                ) : (
                  <SmallTitleText text="Skapa evenemang" />
                )}
                <FormInputField
                  type="select"
                  name="posted_by_id"
                  ref={authorRef}
                  label="Publiceras av"
                  defaultValue={formData.current_event.posted_by_id}
                  items={formData.post_as}
                  itemName="name"
                  required={true}
                />
                <input
                  type="hidden"
                  name="posted_by_type"
                  value={formData.posted_by_type || "band"}
                />
                <FormInputField
                  type="text"
                  name="name"
                  ref={nameRef}
                  label="Namn på evenemang"
                  defaultValue={formData.current_event.name}
                  placeholder="Evenemangets namn"
                  required={true}
                />
                <FormInputField
                  type="datetime-local"
                  name="date"
                  ref={dateRef}
                  label="Datum och tid"
                  defaultValue={formData.current_event.date}
                  min={minEventDateTime}
                  required={true}
                />
                <div className="flex space-x-8">
                  <FormInputField
                    type="file"
                    label="Huvudbild"
                    name="image_main"
                    bottomLabel="Bäst 1280x562px. Max 4mb"
                  />
                  {formData.current_event.image_main && (
                    <div className="h-28">
                      <p className="text-xs text-content">Nuvarande bild</p>
                      <img
                        className="object-contain w-full h-full"
                        src={formData.current_event.image_main}
                      />
                    </div>
                  )}
                </div>
                <label className="form-control max-w-xs flex flex-col my-4">
                  <div className="label">
                    <span className="label-text text-base-content pr-2">
                      Deltagande artister/band
                    </span>
                    <span className="label-text-alt">
                      Sök i databasen eller skriv fritt
                    </span>
                  </div>
                  <input
                    type="text"
                    value={performerInput}
                    onChange={(e) => setPerformerInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addPerformerTags(performerInput);
                      }
                    }}
                    className="input input-bordered max-w-xs"
                    placeholder="Artist- eller bandnamn"
                    list="performers-suggestions"
                    autoComplete="off"
                  />
                  <input
                    type="hidden"
                    name="performers"
                    value={performerTags.map((tag) => tag.name).join(", ")}
                  />
                  <datalist id="performers-suggestions">
                    {performerSuggestions.map((bandName) => (
                      <option key={bandName} value={bandName} />
                    ))}
                  </datalist>
                  {performerTags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 max-w-xs">
                      {performerTags.map((tag) => (
                        <span
                          key={tag.name}
                          className="badge badge-outline px-2 py-3"
                        >
                          <span className={tag.isDb ? "font-semibold" : ""}>
                            {tag.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removePerformerTag(tag.name)}
                            className="ml-2 text-error"
                            aria-label={`Ta bort ${tag.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </label>
                <FormInputField
                  type="text"
                  name="organiser"
                  label="Arrangör"
                  defaultValue={formData.current_event.organiser || ""}
                  placeholder="Arrangörens namn"
                />
                <FormInputField
                  type="text"
                  name="external_link"
                  label="Länk till evenemang"
                  defaultValue={formData.current_event.external_link || ""}
                  placeholder="https://..."
                />
                <FormInputField
                  type="number"
                  name="price"
                  label="Pris (kr)"
                  defaultValue={formData.current_event.price || ""}
                  placeholder="0"
                />
                <FormInputField
                  type="number"
                  name="age_limit"
                  label="Åldersgräns"
                  defaultValue={formData.current_event.age_limit || ""}
                  placeholder="18"
                />

                {formData.regions && (
                  <FormInputField
                    type="select"
                    name="region"
                    label="Region"
                    defaultValue={formData.current_event.region}
                    items={formData.regions}
                    itemName="name"
                  />
                )}
                {/*formData.cities && (
                  
                  <FormInputField
                    type="select"
                    name="city"
                    label="Stad"
                    defaultValue={formData.current_event.city}
                    items={formData.cities}
                    itemName="name"
                  />
                  
                )*/}
                <FormInputField
                  type="text"
                  name="location"
                  label="Plats/Lokal"
                  defaultValue={formData.current_event.location || ""}
                  placeholder="Namn på plats"
                  required={true}
                />
                <RichTextEditorField
                  label="Beskrivning"
                  altLabel="Rubriker, länkar och listor stöds"
                  value={descriptionValue}
                  onChange={setDescriptionValue}
                />
                <div className="flex justify-start gap-4  items-end">
                  <div>
                    {postSuccessMsg && (
                      <p className="font-semibold text-success my-2">
                        {postSuccessMsg}
                      </p>
                    )}
                    {postError && (
                      <h4 className="font-semibold text-error mb-3 pl-1">
                        Fel, {postError}
                      </h4>
                    )}
                    {!postLoading ? (
                      <button className="btn btn-primary" type="submit">
                        {formData.current_event.id ? "Uppdatera" : "Skapa"}
                      </button>
                    ) : (
                      <button
                        className="btn btn-disabled"
                        disabled
                        type="submit"
                      >
                        {formData.current_event.id ? "Uppdatera" : "Skapa"}
                      </button>
                    )}
                  </div>
                  {eventUrl && (
                    <a href={eventUrl} className="btn btn-outline">
                      Visa
                    </a>
                  )}
                  {slug && (
                    <PopUpModulBtn
                      btnText="Ta bort"
                      modalId="DeleteEvent"
                      title="Radera evenemang"
                      btnStyle="btn-neutral border border-base-content/20"
                      text="Det här kommer att ta bort evenemanget och det finns ingen möjlighet att återkalla detta."
                    >
                      <p className="pb-2">Är du säker?</p>
                      <button
                        className="btn btn-error"
                        onClick={handleDeleteEvent}
                      >
                        Ta bort
                      </button>
                    </PopUpModulBtn>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </>
    );
  }
};

export default CreateEventForm;
